import { useEffect } from "react";
import {
  Calendar,
  User,
  ArrowLeft,
  Share2,
  Facebook,
  Instagram,
  Linkedin,
  Link2,
  Video,
} from "lucide-react";
import { notify } from "@/components/common/Notification";
import { type BlogDetailItem } from "../../services/api";

interface BlogDetailPageProps {
  blog: BlogDetailItem;
  onBack: () => void;
}

function formatBlogDate(dateStr: string | null): string {
  if (!dateStr) {
    return "—";
  }
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function BlogDetailPage({ blog, onBack }: BlogDetailPageProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const shareBlog = (platform: string) => {
    const url =
      window.location.origin + window.location.pathname + "?id=" + blog.id;
    const dateStr = formatBlogDate(blog.date);
    const categoryStr = blog.category_name ?? "Uncategorized";

    const shareTitle = `📢 *${blog.title}*`;
    const shareSubtitle = blog.sub_title ? `\n_${blog.sub_title}_` : "";
    const shareMeta = `\n\n📂 *Category:* ${categoryStr}\n🗓️ *Date:* ${dateStr}`;
    const shareBanner = blog.banner ? `\n🖼️ *Banner:* ${blog.banner}` : "";
    const shareDesc = blog.description
      ? `\n\n📝 *Description:*\n${blog.description}`
      : "";

    const fullMessage = `${shareTitle}${shareSubtitle}${shareMeta}${shareBanner}${shareDesc}\n\n🔗 *Read more at:* ${url}`;

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
        break;
      case "instagram":
        shareUrl = `https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullMessage)}`;
        break;
      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(fullMessage)}`;
        break;
      case "copy":
        void navigator.clipboard.writeText(url);
        notify.success("Link copied to clipboard!");
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      {/* Sticky Header with Back Button */}
      <div className="sticky top-[5rem] z-50 border-b border-gray-100 bg-white/80 backdrop-blur-md lg:top-[8.5rem]">
        <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="group flex items-center gap-2 text-gray-600 transition-colors hover:text-[#7A1E1E]"
            >
              <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
              <span className="font-medium">Back to Blogs</span>
            </button>
            <div className="flex items-center gap-4">
              <span className="hidden text-sm text-gray-500 md:block">
                Share this article:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => shareBlog("whatsapp")}
                  className="rounded-full bg-[#21b457]/10 p-2 text-[#21b457] transition-all hover:bg-[#21b457] hover:text-white"
                  title="Share on WhatsApp"
                >
                  <Share2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => shareBlog("copy")}
                  className="rounded-full bg-gray-100 p-2 text-gray-600 transition-all hover:bg-gray-200"
                  title="Copy Link"
                >
                  <Link2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-8">
        {/* Blog Header Card */}
        <article className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
          {/* Hero Media */}
          <div className="relative aspect-[21/9] w-full overflow-hidden bg-black">
            {blog.video ? (
              <div className="h-full w-full">
                {(() => {
                  const videoUrl = blog.video;
                  let videoId = "";
                  if (videoUrl.includes("youtu.be/")) {
                    videoId = videoUrl.split("youtu.be/")[1]?.split("?")[0];
                  } else if (videoUrl.includes("v=")) {
                    videoId = videoUrl.split("v=")[1]?.split("&")[0];
                  } else if (videoUrl.includes("embed/")) {
                    videoId = videoUrl.split("embed/")[1]?.split("?")[0];
                  }

                  if (videoId) {
                    return (
                      <iframe
                        className="h-full w-full"
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=0`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  }
                  return (
                    <div className="flex h-full items-center justify-center text-sm text-white">
                      Invalid Video URL
                    </div>
                  );
                })()}
              </div>
            ) : blog.banner ? (
              <img
                src={blog.banner}
                alt={blog.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 text-gray-400">
                <Video className="h-20 w-20 opacity-20" />
              </div>
            )}

            {/* <div className="absolute left-6 top-6">
              <span className="rounded-full bg-[#D4A017] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
                {blog.category_name ?? "Uncategorized"}
              </span>
            </div> */}
          </div>

          {/* Content Body - Two Column Layout */}
          <div className="px-6 py-10 sm:px-12">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              {/* Main Content Column */}
              <div className="lg:col-span-8">
                <div className="mb-8 border-b border-gray-100 pb-8">
                  <h1 className="mb-6 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
                    {blog.title}
                  </h1>

                  {blog.sub_title && (
                    <p className="text-xl font-medium leading-relaxed text-[#7A1E1E]/80">
                      {blog.sub_title}
                    </p>
                  )}
                </div>

                <div className="prose prose-lg prose-red max-w-none">
                  <div className="space-y-6 text-lg leading-relaxed text-gray-700">
                    {blog.description?.split("\n").map((para: string) => (
                      <p key={para} className="whitespace-pre-wrap">
                        {para}
                      </p>
                    )) ?? "No description available."}
                  </div>
                </div>
              </div>

              {/* Sidebar Column */}
              <div className="lg:col-span-4">
                <div className="sticky top-44 space-y-8">
                  {/* Author & Date Card */}
                  <div className="rounded-2xl border border-gray-100 bg-[#FFF9F0] p-6 shadow-sm">
                    <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-gray-400">
                      Post Information
                    </h3>
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#7A1E1E] text-white shadow-md">
                          <User className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase text-gray-500">
                            Author
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            JBF Team
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-100 bg-white text-[#D4A017] shadow-sm">
                          <Calendar className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase text-gray-500">
                            Published
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatBlogDate(blog.date)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-200/50 pt-4">
                        <div className="text-xs font-medium uppercase text-gray-500">
                          Category
                        </div>
                        <span className="inline-block rounded-lg bg-[#7A1E1E]/10 px-3 py-1 text-sm font-bold text-[#7A1E1E]">
                          {blog.category_name ?? "Uncategorized"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Share Card */}
                  <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                    <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-gray-400">
                      Share This Article
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => shareBlog("whatsapp")}
                        className="flex items-center justify-center gap-2 rounded-xl bg-[#21b457]/10 p-3 text-[#21b457] transition-all hover:bg-[#21b457] hover:text-white"
                        title="WhatsApp"
                      >
                        <Share2 className="h-4 w-4" />
                        <span className="text-xs font-bold">WhatsApp</span>
                      </button>
                      <button
                        onClick={() => shareBlog("facebook")}
                        className="flex items-center justify-center gap-2 rounded-xl bg-[#1877F2]/10 p-3 text-[#1877F2] transition-all hover:bg-[#1877F2] hover:text-white"
                        title="Facebook"
                      >
                        <Facebook className="h-4 w-4" />
                        <span className="text-xs font-bold">Facebook</span>
                      </button>
                      <button
                        onClick={() => shareBlog("instagram")}
                        className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-tr from-[#FFB000]/10 via-[#FD1D1D]/10 to-[#833AB4]/10 p-3 text-[#FD1D1D] transition-all hover:from-[#FFB000] hover:via-[#FD1D1D] hover:to-[#833AB4] hover:text-white"
                        title="Instagram"
                      >
                        <Instagram className="h-4 w-4" />
                        <span className="text-xs font-bold">Instagram</span>
                      </button>
                      <button
                        onClick={() => shareBlog("linkedin")}
                        className="flex items-center justify-center gap-2 rounded-xl bg-[#0A66C2]/10 p-3 text-[#0A66C2] transition-all hover:bg-[#0A66C2] hover:text-white"
                        title="LinkedIn"
                      >
                        <Linkedin className="h-4 w-4" />
                        <span className="text-xs font-bold">LinkedIn</span>
                      </button>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  {/* <button
                    onClick={onBack}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-gray-100 bg-white py-4 font-bold text-gray-600 transition-all hover:border-[#7A1E1E] hover:text-[#7A1E1E]"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    Back to List
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
